import {
  Avatar,
  ButtonGroup,
  Directions,
  Divider,
  Label,
  Menu,
  Sizes,
  Text,
} from '@sede-enterprise/shell-ds-react-framework';
import {
  Moon,
  Sun,
  SunMoon,
} from '@sede-enterprise/shell-ds-react-framework/build/esm/components/Icon/components';
import { AuthenticationScheme } from '@see-power-pft-common-js/auth/enums';
import { useAuth } from 'common-react/auth/AuthProvider';
import {
  redirectToCipm3ChangePassword,
  redirectToCipm3Profile,
} from 'common-react/auth/schemes/cipm3/cipm3';
import {
  redirectToCipm4ChangePassword,
  redirectToCipm4Profile,
} from 'common-react/auth/schemes/cipm4/cipm4';
import { useAppUserDetailsFromJwt } from 'common-react/auth/utils/hooks/useAppUserDetailsFromJwt/useAppUserDetailsFromJwt';
import { ProfileButton } from 'components/NavBar/NavBar.styled';
import { KeyIcon } from 'components/NavBar/NavBarIcons/KeyIcon';
import { NavigationIcon } from 'components/NavBar/NavBarIcons/NavigationIcon';
import { PersonIcon } from 'components/NavBar/NavBarIcons/PersonIcon';
import { DYNATRACE_ACTIONS } from 'constants/dynatrace-actions';
import { useState } from 'react';
import { useStore } from 'store';
import { AppThemeType, ThemeModeType } from 'store/types';
import {
  dynatraceTrackInstantAction,
  useDynatraceTriggerAction,
} from 'utils/dynatrace';

const onOpenPersonalDetails = () => {
  dynatraceTrackInstantAction(DYNATRACE_ACTIONS.PERSONAL_DETAILS);
};
const onOpenChangePassword = () => {
  dynatraceTrackInstantAction(DYNATRACE_ACTIONS.CHANGE_PASSWORD);
};

const AuthenticationSchemeProfileButton = ({
  appTheme,
  authenticationScheme,
}: {
  appTheme: AppThemeType;
  authenticationScheme: AuthenticationScheme | undefined;
}) => {
  if (
    authenticationScheme === AuthenticationScheme.CIPM3 ||
    authenticationScheme === AuthenticationScheme.CIPM4
  ) {
    const { redirectToCipmProfile, redirectToCipmChangePassword } = (() => {
      if (authenticationScheme === AuthenticationScheme.CIPM3) {
        return {
          redirectToCipmProfile: redirectToCipm3Profile,
          redirectToCipmChangePassword: redirectToCipm3ChangePassword,
        };
      }

      if (authenticationScheme === AuthenticationScheme.CIPM4) {
        return {
          redirectToCipmProfile: redirectToCipm4Profile,
          redirectToCipmChangePassword: redirectToCipm4ChangePassword,
        };
      }

      return authenticationScheme satisfies never;
    })();

    return (
      <>
        <ProfileButton
          tabIndex={0}
          type="button"
          role="menuitem"
          aria-labelledby="avatarButton"
          onClick={() => redirectToCipmProfile(onOpenPersonalDetails)}
          data-testid="personal-details"
        >
          <PersonIcon theme={appTheme} />
          Personal Details
        </ProfileButton>
        <ProfileButton
          tabIndex={0}
          type="button"
          role="menuitem"
          aria-labelledby="avatarButton"
          onClick={() => redirectToCipmChangePassword(onOpenChangePassword)}
          data-testid="change-password"
        >
          <KeyIcon theme={appTheme} />
          Change Password
        </ProfileButton>
      </>
    );
  }

  if (
    authenticationScheme === undefined ||
    authenticationScheme === AuthenticationScheme.DEV ||
    authenticationScheme === AuthenticationScheme.DEPSSO
  ) {
    return null;
  }

  authenticationScheme satisfies never;
};

export const ProfileMenu = () => {
  const { authStoreAccess, logout } = useAuth();

  const authenticationScheme = authStoreAccess.getAuthenticationScheme();

  const [themeMode, setThemeMode, appTheme] = useStore((state) => [
    state.themeMode,
    state.setThemeMode,
    state.appTheme,
  ]);

  const { firstName, lastName, email } = useAppUserDetailsFromJwt();

  const capitalisedGlobalTheme =
    themeMode.charAt(0).toUpperCase() + themeMode.slice(1);

  const [selectedThemeKey, setSelectedThemeKey] = useState<string>(
    themeMode ? capitalisedGlobalTheme : 'Light',
  );

  const handleThemeSelection = (selectedValues: string) => {
    const lowerCasedSelectedValue =
      selectedValues.toLowerCase() as ThemeModeType;
    setSelectedThemeKey(selectedValues);
    setThemeMode(lowerCasedSelectedValue);
  };

  const isNameValid = Boolean(firstName && lastName);

  useDynatraceTriggerAction(DYNATRACE_ACTIONS.PROFILE_MENU);

  return (
    <>
      <Avatar
        style={{ padding: '14px' }}
        abbreviation={isNameValid ? `${firstName?.[0]}${lastName?.[0]}` : ''}
        label={isNameValid ? `${firstName} ${lastName}` : ''}
        secondaryLabel={email ? `${email}` : ''}
        aria-label="Profile avatar"
      />
      <Menu id="profileMenu">
        <AuthenticationSchemeProfileButton
          appTheme={appTheme}
          authenticationScheme={authenticationScheme}
        />
        <ProfileButton
          tabIndex={0}
          type="button"
          role="menuitem"
          aria-labelledby="avatarButton"
          onClick={logout}
          data-testid="sign-out"
        >
          <NavigationIcon theme={appTheme} />
          Sign Out
        </ProfileButton>
      </Menu>
      <Divider
        style={{ margin: '8px 0 16px' }}
        direction={Directions.Horizontal}
        prominence={'subtle'}
        size={Sizes.Small}
      />
      <Label size={Sizes.Medium} bold>
        Theme Preferences
      </Label>
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          margin: '16px 0',
        }}
      >
        <ButtonGroup
          type="radio"
          data-testid="theme-preferences"
          size={Sizes.Large}
          selectedKey={selectedThemeKey}
          onChange={handleThemeSelection}
          items={[
            {
              label: 'Light',
              icon: <Sun />,
            },
            {
              label: 'Dark',
              icon: <Moon />,
            },
            {
              label: 'System',
              icon: <SunMoon />,
            },
          ]}
        />
      </div>
      <Divider
        direction={Directions.Horizontal}
        prominence={'subtle'}
        size={Sizes.Small}
      />
      <div style={{ padding: '24px 14px' }}>
        <Text prominence={'subtle'} size={Sizes.Small}>
          Customer service is open Mon - Fri, 9am to 5pm
        </Text>
        <Text prominence={'subtle'} size={Sizes.Small}>
          +4420 8396 6848
        </Text>
        <Text prominence={'subtle'} size={Sizes.Small}>
          support@limejump.com
        </Text>
      </div>
    </>
  );
};

export default ProfileMenu;
